import React, { useEffect, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { UserRole } from '@bloom/shared';
import { useAuthStore, useIsAuthenticated, useUser } from '../../store/useAuthStore';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: UserRole[];
  redirectTo?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  allowedRoles,
  redirectTo = '/login',
}) => {
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();
  const user = useUser();
  const { getCurrentUser, isLoading } = useAuthStore();

  useEffect(() => {
    // If we have a token but no user data, try to fetch user
    // Check localStorage for token (handles impersonation flow where Zustand state is cleared)
    const hasToken = !!localStorage.getItem('bloom_token');
    if (!user && hasToken && !isLoading) {
      getCurrentUser();
    }
  }, [user, isLoading, getCurrentUser]);

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="animate-spin h-8 w-8 mx-auto text-blue-600 mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  // But first check if we have a token - if so, wait for getCurrentUser() to complete
  const hasToken = !!localStorage.getItem('bloom_token');
  if (!isAuthenticated || !user) {
    // If we have a token, show loading while we validate it
    if (hasToken) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <Loader2 className="animate-spin h-8 w-8 mx-auto text-blue-600 mb-4" />
            <p className="text-gray-600">Authenticating...</p>
          </div>
        </div>
      );
    }
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Redirect pending users to pending approval dashboard
  // Allow them to access this route only, redirect from all others
  if (user.status === 'pending' && location.pathname !== '/pending-approval') {
    return <Navigate to="/pending-approval" replace />;
  }

  // Redirect physicians with missing required profile info to complete-profile
  // Allow access to complete-profile, id-proofing, and logout routes
  const profileExemptPaths = ['/complete-profile', '/id-proofing', '/pending-approval'];
  const isExemptPath = profileExemptPaths.some(path => location.pathname.startsWith(path));
  if (user.role === 'physician' && !isExemptPath) {
    const missingClinicName = !user.profile?.clinicName;
    const missingClinicAddress = !user.physicianData?.clinicAddress?.street;
    if (missingClinicName || missingClinicAddress) {
      return <Navigate to="/complete-profile" replace />;
    }
  }

  // Check role-based access if roles are specified
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8 text-center">
          <div className="text-red-500 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">
            You don't have permission to access this page.
          </p>
          <p className="text-sm text-gray-500">
            Your role: <span className="font-medium capitalize">{user.role.replace('_', ' ')}</span>
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}; 